/**
 * useVersionCheck — polls /version.json every 60 s and signals when
 * the live version differs from the version bundled into this build.
 *
 * On refresh the new bundle loads with the new version → banner gone.
 */
import { useState, useEffect } from 'react';
import { CHANGELOG } from '../game/CHANGELOG';

const BUNDLED_VERSION = CHANGELOG[0]?.version ?? 'unknown';
const POLL_MS         = 60_000; // check every 60 seconds

// BETA 23.10 — the ELECTRON APP is a frozen bundle: "refresh" can never
// update it, so the old banner nagged forever with an unsatisfiable loop
// (Dylan: "even if i click update it pops back up with the same push").
export function isElectronApp(): boolean {
  try { return /electron/i.test(navigator.userAgent); } catch { return false; }
}

const DISMISS_KEY = 'gz_dismissed_update_version';

export function useVersionCheck() {
  const [liveVersion, setLiveVersion]   = useState<string | null>(null);
  const [dismissed,   setDismissed]     = useState(false);

  useEffect(() => {
    let alive = true;

    async function poll() {
      try {
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (!res.ok || !alive) return;
        const data = await res.json() as { version?: string };
        if (data.version && data.version !== BUNDLED_VERSION) {
          // BETA 23.10 — a version the player already dismissed stays
          // dismissed forever (persisted), instead of re-nagging each poll.
          let alreadyDismissed = false;
          try { alreadyDismissed = localStorage.getItem(DISMISS_KEY) === data.version; } catch { /* private mode */ }
          if (!alreadyDismissed) setLiveVersion(data.version);
        }
      } catch {
        // network error — silently ignore, try again next interval
      }
    }

    // First check 45 s after load (user just refreshed — no point checking immediately)
    const first    = setTimeout(poll, 45_000);
    const interval = setInterval(poll, POLL_MS);

    return () => {
      alive = false;
      clearTimeout(first);
      clearInterval(interval);
    };
  }, []);

  return {
    updateAvailable: !!liveVersion && !dismissed,
    liveVersion,
    dismiss: () => {
      setDismissed(true);
      // BETA 23.10 — persist so this version never nags again
      try { if (liveVersion) localStorage.setItem(DISMISS_KEY, liveVersion); } catch { /* private mode */ }
    },
  };
}
